import { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  useMediaQuery,
  Checkbox,
  styled,
} from '@mui/material';
import { faEdit, faTrashAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IDeaEditMode,
  FieldRequiredIndicator,
  variables,
  ErrorIcon,
  Tooltip,
  TooltipTrigger,
  StyledButton,
  roles,
  roleLabel,
  formatDate,
  formatDateUTC,
  csosUpmApi,
  IPreFilter,
  useUserContext,
} from '../common';
import {
  isEmpty,
  isNil,
  compose as lcompose,
  defaultTo as ldefaultTo,
} from 'lodash/fp';
import { flow } from 'fp-ts/lib/function';

// Helper to check the API for a single DEA number
export const lookupDeaExpiration = async (deaNum: string, icamHeaders: any): Promise<string | null> => {
  const payload: IPreFilter = {
    chainCodeFlag: true,
    deaNumber: deaNum, // Search specifically for the typed number
    ssn: null, taxId: null, npi: null, city: null, state: null, postalCd: null,
  };

  try {
    const response = await csosUpmApi(icamHeaders).post(
      `v1/api/upm/legacy/csa-search?page=0&size=1`,
      payload
    );
    // Grab the first record from the content array matching our search
    const records = response?.data?.content || [];
    if (records.length > 0) {
      return records[0].expirationDate; // Return the expiration date found from the API
    }
    return null;
  } catch (error) {
    console.error("Failed fetching DEA expiration", error);
    return null;
  }
};

export function RegistrationNo({
  item: { deaNum, editMode, key, msgText, selected, role, expDt, disabled, serialNumber, fetchStatus },
  editing,
  save,
  remove,
  select,
  tooltipValue,
  toggleTooltip,
  offTooltip,
  feedback,
}: {
  item: IDeaEditMode;
  save?: (a: IDeaEditMode) => void;
  remove?: (key: string) => void;
  editing?: (key: string) => void;
  select?: ({ checked, key }: { checked: boolean; key: string }) => void;

  tooltipValue: (tooltip: string) => boolean;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
  feedback: string;
}) {
  const { user } = useUserContext();
  const [newNo, setNewNo] = useState(deaNum);
  const [newNoIsChanged, setNewNoIsChanged] = useState(false);

  const handleSave = async () => {
    if (!save) return;

    let fetchedExpDt = null;

    if (editMode === 'Create' && newNo) {
      // 1. Fire the exact same API search wrapper using the user headers
      fetchedExpDt = await lookupDeaExpiration(newNo, user.icam!.headers);
    }

    // 2. Dispatch the item to the reducer carrying the real API expDt
    save({
      key,
      deaNum: newNo,
      editMode: 'Read',
      msgCode: null,
      msgText: fetchedExpDt ? null : 'Added manually (No matching registry date found)',
      serialNumber: null,
      expDt: fetchedExpDt || expDt,
    });

    if (editMode === 'Create') {
      setNewNo('');
    }
    setNewNoIsChanged(false);
  };

  useEffect(() => {
    if (
      feedback.endsWith('added') &&
      !!document.querySelector('#new-dea-reg-no')
    ) {
      (document.querySelector('#new-dea-reg-no') as HTMLElement).focus();
    }
  }, [feedback]);

  switch (editMode) {
    case 'Create':
      return (
        <CreateWrapper>
          <DeaRegNoLabel htmlFor={`new-dea-reg-no`} />

          <Tooltip name="requestNewDeaNum" tooltipValue={tooltipValue}>
            <TextField
              id={`new-dea-reg-no`}
              value={newNo}
              onChange={({ target: { value } }) =>
                flow(setNewNo, () => setNewNoIsChanged(true))(value)
              }
              error={newNoIsChanged && isEmpty(newNo)}
              variant="outlined"
            />
          </Tooltip>
          <TooltipTrigger
            name="requestNewDeaNum"
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
          />

          <CenterButton
            variant="contained"
            onClick={handleSave}
            disabled={isEmpty(newNo)}>
            add
          </CenterButton>
        </CreateWrapper>
      );

    case 'Update':
      return (
        <LiWrapper>
          <DeaRegNoLabel htmlFor={key} />
          <TextField
            id={key}
            error={isEmpty(deaNum)}
            value={newNo}
            onChange={({ target: { value } }) =>
              flow(setNewNo, () => setNewNoIsChanged(true))(value)
            }
            variant="outlined"
          />
          <span></span>
          <CenterButton
            variant="contained"
            onClick={handleSave}
            disabled={isEmpty(deaNum)}>
            Update
          </CenterButton>
        </LiWrapper>
      );

    case 'Read':
      return (
        <LiWrapper>
          <DeaRegNoLabel htmlFor="" />
          <span>{deaNum}</span>
          <span></span>
          <ReadActions>
            <StyledButton
              onClick={() => {
                editing && editing(key);
              }}>
              <span>Update</span>
              <FontAwesomeIcon icon={faEdit} size="1x" fixedWidth />
            </StyledButton>

            <StyledButton
              onClick={() => {
                remove && remove(key);
              }}>
              <span>Remove</span>
              <FontAwesomeIcon icon={faTrashAlt} size="1x" fixedWidth />
            </StyledButton>
          </ReadActions>

          {fetchStatus === 'loading' ? (
            <>
              <DeaRegNoExpiration />
              <span>{formatDate(expDt)}</span>
            </>
          ) : isNil(expDt) ? null : (
            <>
              <DeaRegNoExpiration />
              <ExpirationValue>{formatDateUTC(expDt)}</ExpirationValue>
            </>
          )}
          
    


          {!isEmpty(msgText) && (
            <DeaNumMsg>
              <ErrorIcon />
              {msgText}
            </DeaNumMsg>
          )}
        </LiWrapper>
      );

    case 'Select':
      return (
        <LiWrapper>
          <DeaRegNoLabel htmlFor="" />
          <span>{deaNum}</span>
          <ReadActions>
            <Checkbox
              checked={selected}
              disabled={disabled}
              onChange={(_, checked) => select && select({ checked, key })}
              name="selectDeaNum"
              color="primary"
            />
          </ReadActions>
          <span></span>
          {!(expDt === null || expDt === void 0) && (
            <ExpirationRow>
              <label style={{ fontSize: '0.8em' }}>Expiration Date:</label>
              <span>{formatDateUTC(expDt)}</span>
            </ExpirationRow>
          )}
          <label style={{ fontSize: '0.8em' }}>Role:</label>
          <span style={{ fontSize: '0.8em' }}>
            {lcompose(roleLabel(roles), ldefaultTo(''))(role)}
          </span>
          <label style={{ fontSize: '0.8em', gridColumn: '1 / 2', justifySelf: 'end' }}>Serial No:</label>
          <span style={{ fontSize: '0.8em' }}>
            {serialNumber ?? ''}
          </span>

          {!isEmpty(msgText) && (
            <DeaNumMsg>
              <ErrorIcon />
              {msgText}
            </DeaNumMsg>
          )}
        </LiWrapper>
      );

    default:
      return <>NA</>;
  }
}

const wrapper = () => ({
  display: 'grid',
  gridTemplateColumns: '4fr 4fr 28px 2fr',
  gap: `1rem`,
  alignItems: 'center',

  '@media (min-width: 40em)': {
    gridTemplateColumns: '3fr 2fr 28px 1fr',
  },

  '& > label': {
    justifySelf: 'end',
  },

  border: variables.cardBorder,
  borderRadius: variables.cardBorderRadius,

  padding: '0.5rem',

  transition: '0.3s ease',
});

const CreateWrapper = styled('article')(({ theme }) => ({
  ...wrapper(),

  boxSizing: 'border-box',
  '@media (min-width: 40em)': { width: '40rem' },
}));

const LiWrapper = styled('li')(({ theme }) => ({
  ...wrapper(),
}));

const CenterButton = styled(Button)(({ theme }) => ({ justifySelf: 'center' }));

const ReadActions = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const DeaNumMsg = styled('span')(({ theme }) => ({
  gridColumn: '1/-1',
  justifySelf: 'center',
}));

function DeaRegNoLabel({ htmlFor }: { htmlFor: string }) {
  const matchesTabsBreakpoint = useMediaQuery('(min-width:40em)', {
    noSsr: true,
  });

  const label = matchesTabsBreakpoint ? 'DEA Registration #' : 'DEA Reg No';
  return (
    <L htmlFor={htmlFor}>
      {label} <FieldRequiredIndicator />:
    </L>
  );
}

function DeaRegNoExpiration() {
  return <L style={{ gridRow: 2, gridColumn: 1, marginTop: '-0.35rem', lineHeight: 1,   paddingBottom: '12px' }}>Expiration Date:</L>;
}

const ExpirationValue = styled('span')(({ theme }) => ({
  gridRow: 2,
  gridColumn: 2,
  marginTop: '-0.35rem',
  lineHeight: 1,
  paddingBottom: '12px',
  fontSize: '0.95em',
  color: '#222'
}));

const L = styled('label')(({ theme }) => ({ fontWeight: 500 }));

const ExpirationRow = styled('span')(({ theme }) => ({
  gridColumn: '1/ -1',
  display: 'flex',
  gap: '0.5rem',
  // paddingLeft: '1rem',
  fontSize: '0.9rem',
  color: '#555',

  '@media (min-width: 40em)': {
    paddingLeft: 'calc(3fr / 5 + 1.5rem)',
  },
  '@media (max-width: 40em)': {
    paddingLeft: 'calc(4fr / 5 + 1rem)',
  }
}));
